import { useState, useCallback, useEffect, useRef } from 'react';
import { Teams } from '../api';
import { CacheKeysTables, KeyMetricsCache2 } from '../constants/keyCache';
import { getQueryCacheForTable } from '../helpers/queryCacheKey';
import { queryClient } from '../helpers/queryClient';

export const useKeyMetrics = (selectedTeam, playersGroup, sortBy, sortOrder, playerNameSearch) => {
  const [keyMetricsStrength, setKeyMetricsStrength] = useState([]);
  const [keyMetricsFatigue, setKeyMetricsFatigue] = useState<any[]>([]);
  const [loadingTableStrength, setLoadingTableStrength] = useState(true);
  const [loadingTableFatigue, setLoadingTableFatigue] = useState(true);
  const [paginationStrength, setPaginationStrength] = useState({ currentPage: 1, recordsPerPage: 20, totalItems: 0, totalPages: 0 });
  const [paginationFatigue, setPaginationFatigue] = useState({ currentPage: 1, recordsPerPage: 20, totalItems: 0, totalPages: 0 });
  const [selectedTeamId, setSelectedTeamId] = useState(selectedTeam?._id || null);
  const [currentPageStrengthState, setCurrentPageStrength] = useState(1);
  const [currentPageFatigueState, setCurrentPageFatigue] = useState(1);
  const [currentPlayerGroup, setCurrentPlayerGroup] = useState('');
  const [currentRecordPerPageStrength, setCurrentRecordPerPageStrength] = useState(20);
  const [currentRecordPerPageFatigue, setCurrentRecordPerPageFatigue] = useState(20);
  const [currentPlayerNameSearch, setCurrentPlayerNameSearch] = useState(playerNameSearch);
  const [currentSortOrder, setCurrentSortOrder] = useState(sortOrder);

  const TEAMS = useRef(new Teams()).current;

  const fetchKeyMetrics = useCallback(async () => {
    setLoadingTableStrength(true);
    setLoadingTableFatigue(true);
    let currentPageStrength: number = 0, currentPageFatigue: number = 0, currentRecordPerPageStrength: number = 0, currentRecordPerPageFatigue: number = 0;
    try {
      const isSameTeam = selectedTeamId === selectedTeam._id;
      const isSameCurrentPageStrength = currentPageStrengthState === paginationStrength.currentPage;
      const isSameCurrentPageFatigue = currentPageFatigueState === paginationFatigue.currentPage;
      const isSameRecordsPerPageStrength = currentRecordPerPageStrength === paginationStrength.recordsPerPage;
      const isSameRecordsPerPageFatigue = currentRecordPerPageFatigue === paginationFatigue.recordsPerPage;
      const isSamePlayerNameSearch = currentPlayerNameSearch === playerNameSearch;
      const isSameSortOrder = currentSortOrder === sortOrder;
      const isSamePlayerGroup = currentPlayerGroup === playersGroup;
      if(
        isSameTeam &&
        isSameCurrentPageStrength &&
        isSameCurrentPageFatigue &&
        isSamePlayerNameSearch &&
        isSameSortOrder &&
        isSameRecordsPerPageStrength &&
        isSameRecordsPerPageFatigue &&
        isSamePlayerGroup
      )
        return;
      currentPageStrength = isSameTeam ? isSamePlayerNameSearch ? paginationStrength.currentPage : 1 : 1;
      currentPageFatigue = isSameTeam ? isSamePlayerNameSearch ? paginationFatigue.currentPage : 1 : 1;
      currentRecordPerPageStrength = isSameTeam ? isSamePlayerNameSearch ? paginationStrength.recordsPerPage : 20 : 20;
      currentRecordPerPageFatigue = isSameTeam ? isSamePlayerNameSearch ? paginationFatigue.recordsPerPage : 20 : 20;
      const dataStrength = getQueryCacheForTable({
        selectedTeam,
        playersGroup,
        page: currentPageStrength,
        recordsPerPage: currentRecordPerPageStrength,
        sortBy,
        sortOrder,
        mainCacheKey: KeyMetricsCache2.StrengthRecovery,
        playerNameSearch,
      })

      const dataFatigue = getQueryCacheForTable({
        selectedTeam,
        playersGroup,
        page: currentPageFatigue,
        recordsPerPage: currentRecordPerPageFatigue,
        sortBy,
        sortOrder,
        mainCacheKey: KeyMetricsCache2.Fatigue,
        playerNameSearch,
      })
      const [strengthResponse, fatigueResponse] = await Promise.all([
        TEAMS.ExecuteCacheRequest({
          isForceDelete: !isSamePlayerNameSearch,
          cacheKey: dataStrength.cacheKey,
          queryFn: () => TEAMS.GetKeyMetrics(
            selectedTeam._id,
            playersGroup,
            currentPageStrength,
            paginationStrength.recordsPerPage,
            sortBy,
            sortOrder,
            playerNameSearch
          )
        }),
        TEAMS.ExecuteCacheRequest({
          isForceDelete: !isSamePlayerNameSearch,
          cacheKey: dataFatigue.cacheKey,
          queryFn: () => TEAMS.GetPostExams(
            selectedTeam._id,
            playersGroup,
            currentPageFatigue,
            paginationFatigue.recordsPerPage,
            ['playerName', 'date'].includes(sortBy) ? sortBy : 'date',
            sortOrder,
            playerNameSearch
          )
        })
      ]);


      if (strengthResponse.status) {
        setKeyMetricsStrength(strengthResponse.data.data);
        setPaginationStrength(prev => ({
          ...prev,
          currentPage: currentPageStrength,
          totalItems: strengthResponse.data.pagination.totalItems,
          totalPages: strengthResponse.data.pagination.totalPages,
        }));
      }

      if (fatigueResponse.status) {
        setKeyMetricsFatigue(fatigueResponse.data.data.entries);
        setPaginationFatigue(prev => ({
          ...prev,
          currentPage: currentPageFatigue,
          totalItems: fatigueResponse.data.data.pagination.totalItems,
          totalPages: fatigueResponse.data.data.pagination.totalPages
        }));
      }
    } catch (error) {
      console.error('Error fetching key metrics:', error);
    } finally {
      setLoadingTableStrength(false);
      setLoadingTableFatigue(false);
    }
    setSelectedTeamId(selectedTeam._id);
    setCurrentPageStrength(currentPageStrength);
    setCurrentPageFatigue(currentPageFatigue);
    setCurrentPlayerNameSearch(playerNameSearch);
    setCurrentSortOrder(sortOrder);
    setCurrentRecordPerPageStrength(currentRecordPerPageStrength);
    setCurrentRecordPerPageFatigue(currentRecordPerPageFatigue);
    setCurrentPlayerGroup(playersGroup);
  }, [selectedTeam._id, playersGroup, paginationStrength.currentPage, paginationStrength.recordsPerPage, paginationFatigue.currentPage, paginationFatigue.recordsPerPage, sortBy, sortOrder, TEAMS, playerNameSearch]);

  useEffect(() => {
    // Re-fetch key metrics whenever the selected team (id or special "__name" like "All Players")
    // or any of the main query parameters change.
    fetchKeyMetrics();
  }, [
    selectedTeam._id,
    selectedTeam?.__name,
    playersGroup,
    sortBy,
    sortOrder,
    paginationStrength.currentPage,
    paginationStrength.recordsPerPage,
    paginationFatigue.currentPage,
    paginationFatigue.recordsPerPage,
    playerNameSearch,
  ]);

  useEffect(() => {
    setPaginationStrength(prev => ({ ...prev, currentPage: 1 }));
    setPaginationFatigue(prev => ({ ...prev, currentPage: 1 }));
  },[playersGroup])

  const updatePaginationStrength = useCallback((newPage, newRecordsPerPage) => {
    setPaginationStrength(prev => ({
      ...prev,
      currentPage: newPage || prev.currentPage,
      recordsPerPage: newRecordsPerPage || prev.recordsPerPage
    }));
  }, []);

  const updatePaginationFatigue = useCallback((newPage, newRecordsPerPage) => {
    setPaginationFatigue(prev => ({
      ...prev,
      currentPage: newPage || prev.currentPage,
      recordsPerPage: newRecordsPerPage || prev.recordsPerPage
    }));
  }, []);

  return {
    keyMetricsStrength,
    keyMetricsFatigue,
    loadingTableStrength,
    loadingTableFatigue,
    paginationStrength,
    paginationFatigue,
    updatePaginationStrength,
    updatePaginationFatigue,
    fetchKeyMetrics,
  };
};
